import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid, Legend, Cell } from 'recharts';
import { Megaphone, RefreshCw, Calendar, TrendingUp, DollarSign, MousePointer, Eye, Target } from 'lucide-react';
import api from '../utils/api';
import { formatNumber, formatMoney } from '../utils/constants';

const tooltipStyle = { background: '#1c2235', border: '1px solid #252d3d', borderRadius: 8, fontFamily: 'Raleway', color: '#e8ecf4' };
const tickStyle = { fill: '#c8d0e0', fontSize: 11 };
const campaignColors = ['#2E7DE2', '#22c55e', '#f97316', '#a855f7', '#eab308', '#ef4444', '#06b6d4', '#ec4899'];

export default function Campaigns() {
  const [data, setData] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [preset, setPreset] = useState('last_30d');

  useEffect(() => {
    api.get('/campaigns/sync-status').then(res => setSyncStatus(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    let params = {};
    const today = new Date();
    if (preset === 'last_7d') {
      const d = new Date(today); d.setDate(d.getDate() - 7);
      params = { start_date: d.toISOString().split('T')[0], end_date: today.toISOString().split('T')[0] };
    } else if (preset === 'last_30d') {
      const d = new Date(today); d.setDate(d.getDate() - 30);
      params = { start_date: d.toISOString().split('T')[0], end_date: today.toISOString().split('T')[0] };
    } else if (preset === 'last_90d') {
      const d = new Date(today); d.setDate(d.getDate() - 90);
      params = { start_date: d.toISOString().split('T')[0], end_date: today.toISOString().split('T')[0] };
    } else if (preset === 'this_month') {
      params = { start_date: `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-01`, end_date: today.toISOString().split('T')[0] };
    } else if (preset === 'last_month') {
      const d = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const e = new Date(today.getFullYear(), today.getMonth(), 0);
      params = { start_date: d.toISOString().split('T')[0], end_date: e.toISOString().split('T')[0] };
    } else if (preset === 'custom' && dateRange.start && dateRange.end) {
      params = { start_date: dateRange.start, end_date: dateRange.end };
    }
    api.get('/campaigns/summary', { params }).then(res => setData(res.data)).catch(() => {});
  }, [preset, dateRange]);

  useEffect(() => {
    if (selectedCampaign) {
      const params = {};
      if (preset !== 'custom') {
        const today = new Date();
        const d = new Date(today);
        if (preset === 'last_7d') d.setDate(d.getDate() - 7);
        else if (preset === 'last_30d') d.setDate(d.getDate() - 30);
        else if (preset === 'last_90d') d.setDate(d.getDate() - 90);
        params.start_date = d.toISOString().split('T')[0];
        params.end_date = today.toISOString().split('T')[0];
      } else if (dateRange.start && dateRange.end) {
        params.start_date = dateRange.start;
        params.end_date = dateRange.end;
      }
      api.get(`/campaigns/daily/${selectedCampaign}`, { params }).then(res => setDailyData(res.data)).catch(() => {});
    }
  }, [selectedCampaign, preset, dateRange]);

  const statCards = data?.totals ? [
    { label: 'Inversión total', value: formatMoney(data.totals.total_cost), icon: DollarSign, color: '#22c55e' },
    { label: 'Impresiones', value: formatNumber(data.totals.total_impressions), icon: Eye, color: '#2E7DE2' },
    { label: 'Clicks', value: formatNumber(data.totals.total_clicks), icon: MousePointer, color: '#f97316' },
    { label: 'CTR promedio', value: (parseFloat(data.totals.avg_ctr) || 0).toFixed(2) + '%', icon: TrendingUp, color: '#a855f7' },
    { label: 'CPC promedio', value: formatMoney(data.totals.avg_cpc), icon: DollarSign, color: '#eab308' },
    { label: 'Conversiones', value: formatNumber(data.totals.total_conversions), icon: Target, color: '#22c55e' },
  ] : [];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Megaphone size={24} color="var(--accent)" />
          <h1 style={{ fontSize: 26, fontWeight: 800 }}>Campañas Google Ads</h1>
        </div>
        {syncStatus && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <RefreshCw size={12} /> Última sync: {syncStatus.last_sync || 'Nunca'} | {syncStatus.total_campaigns} campañas | {syncStatus.total_rows} registros
          </div>
        )}
      </div>

      {/* Date filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center' }}>
        <Calendar size={16} color="var(--text-muted)" />
        {[
          { key: 'last_7d', label: 'Últimos 7 días' },
          { key: 'last_30d', label: 'Últimos 30 días' },
          { key: 'last_90d', label: 'Últimos 90 días' },
          { key: 'this_month', label: 'Este mes' },
          { key: 'last_month', label: 'Mes pasado' },
          { key: 'custom', label: 'Personalizado' },
        ].map(p => (
          <button key={p.key} className={`btn btn-sm ${preset === p.key ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setPreset(p.key)}>{p.label}</button>
        ))}
        {preset === 'custom' && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="date" value={dateRange.start} onChange={e => setDateRange({...dateRange, start: e.target.value})} style={{ width: 150, padding: '6px 10px', fontSize: 12 }} />
            <span style={{ color: 'var(--text-muted)' }}>a</span>
            <input type="date" value={dateRange.end} onChange={e => setDateRange({...dateRange, end: e.target.value})} style={{ width: 150, padding: '6px 10px', fontSize: 12 }} />
          </div>
        )}
      </div>

      {!data || (!data.campaigns?.length && !syncStatus?.total_rows) ? (
        <div className="empty-state">
          <Megaphone size={48} />
          <h3>Sin datos de campañas</h3>
          <p style={{ maxWidth: 400, margin: '0 auto' }}>
            Para ver datos acá, necesitás configurar el script de Google Ads. 
            Andá a Configuración para obtener tu webhook key y las instrucciones.
          </p>
        </div>
      ) : (
        <>
          {/* Stat Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14, marginBottom: 24 }}>
            {statCards.map((s, i) => (
              <div key={i} className="stat-card">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span className="stat-value" style={{ fontSize: 22 }}>{s.value}</span>
                  <s.icon size={20} color={s.color} />
                </div>
                <span className="stat-label">{s.label}</span>
              </div>
            ))}
          </div>

          {/* Campaign spend chart */}
          <div className="card" style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: 'var(--text-primary)' }}>Inversión por campaña</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.campaigns?.slice(0, 10)} layout="vertical" margin={{ left: 20 }}>
                <XAxis type="number" tick={tickStyle} tickFormatter={v => '$' + v.toLocaleString()} />
                <YAxis dataKey="campaign_name" type="category" width={200} tick={tickStyle} />
                <Tooltip contentStyle={tooltipStyle} formatter={v => formatMoney(v)} />
                <Bar dataKey="total_cost" name="Inversión" radius={[0, 6, 6, 0]}>
                  {data.campaigns?.slice(0, 10).map((_, i) => <Cell key={i} fill={campaignColors[i % campaignColors.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Campaigns table with CRM cross-data */}
          <div className="card" style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: 'var(--text-primary)' }}>Detalle de campañas</h3>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Campaña</th>
                    <th>Inversión</th>
                    <th>Impresiones</th>
                    <th>Clicks</th>
                    <th>CTR</th>
                    <th>CPC</th>
                    <th>Conv.</th>
                    <th>Leads CRM</th>
                    <th>Ganados</th>
                    <th>CPL Real</th>
                    <th>CPA</th>
                  </tr>
                </thead>
                <tbody>
                  {data.campaigns?.map((c, i) => (
                    <tr key={i} style={{ cursor: 'pointer' }} onClick={() => setSelectedCampaign(c.campaign_id)}>
                      <td style={{ fontWeight: 600, color: selectedCampaign === c.campaign_id ? 'var(--accent)' : 'var(--text-primary)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.campaign_name}</td>
                      <td style={{ color: 'var(--text-primary)' }}>{formatMoney(c.total_cost)}</td>
                      <td style={{ color: 'var(--text-primary)' }}>{formatNumber(c.total_impressions)}</td>
                      <td style={{ color: 'var(--text-primary)' }}>{formatNumber(c.total_clicks)}</td>
                      <td style={{ color: 'var(--text-primary)' }}>{(parseFloat(c.avg_ctr) || 0).toFixed(2)}%</td>
                      <td style={{ color: 'var(--text-primary)' }}>{formatMoney(c.avg_cpc)}</td>
                      <td style={{ color: 'var(--text-primary)' }}>{formatNumber(c.total_conversions)}</td>
                      <td style={{ color: '#2E7DE2', fontWeight: 600 }}>{c.crm_leads || 0}</td>
                      <td style={{ color: '#22c55e', fontWeight: 600 }}>{c.crm_ganados || 0}</td>
                      <td style={{ color: c.cost_per_lead ? '#eab308' : 'var(--text-muted)', fontWeight: 600 }}>{c.cost_per_lead ? formatMoney(c.cost_per_lead) : '—'}</td>
                      <td style={{ color: c.cost_per_client ? '#22c55e' : 'var(--text-muted)', fontWeight: 600 }}>{c.cost_per_client ? formatMoney(c.cost_per_client) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Daily chart for selected campaign */}
          {selectedCampaign && dailyData.length > 0 && (
            <div className="card">
              <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: 'var(--text-primary)' }}>
                Rendimiento diario: {data.campaigns?.find(c => c.campaign_id === selectedCampaign)?.campaign_name}
              </h3>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={dailyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="date" tick={tickStyle} />
                  <YAxis yAxisId="left" tick={tickStyle} />
                  <YAxis yAxisId="right" orientation="right" tick={tickStyle} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend wrapperStyle={{ color: '#c8d0e0' }} />
                  <Line yAxisId="left" type="monotone" dataKey="clicks" name="Clicks" stroke="#2E7DE2" strokeWidth={2} dot={{ r: 2 }} />
                  <Line yAxisId="right" type="monotone" dataKey="cost" name="Inversión" stroke="#22c55e" strokeWidth={2} dot={{ r: 2 }} />
                  <Line yAxisId="left" type="monotone" dataKey="conversions" name="Conversiones" stroke="#f97316" strokeWidth={2} dot={{ r: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}
    </div>
  );
}
